import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { CellAttachment } from '@/shared/components/Grid/attachments/types';

/**
 * Images and PDFs in attachment cells, stored in R2 behind `/api/files`.
 *
 * A cell keeps only `/api/files/<id>` URLs. Those need the session's bearer
 * token, which an `<img src>` cannot send, so they are fetched here and shown
 * through object URLs, cached for the life of the tab.
 */

export const FILE_URL_PREFIX = '/api/files/';

/** Longest side of an uploaded photo. Receipts stay legible; phone photos shrink ~10×. */
const MAX_IMAGE_SIDE = 2000;
const THUMB_SIDE = 256;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'] as const;
export const ACCEPT_ATTRIBUTE = ACCEPTED.join(',');

export type UploadFailure =
  | { readonly reason: 'plan_limit'; readonly key: string; readonly limit: string }
  | { readonly reason: 'type' }
  | { readonly reason: 'forbidden' }
  | { readonly reason: 'email_unverified' }
  | { readonly reason: 'rate_limited' }
  | { readonly reason: 'failed' };

export class UploadError extends Error {
  constructor(readonly failure: UploadFailure) {
    super(failure.reason);
    this.name = 'UploadError';
  }
}

async function bearer(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new UploadError({ reason: 'forbidden' });
  return token;
}

export function isStoredFileUrl(url: string | undefined): url is string {
  return typeof url === 'string' && url.startsWith(FILE_URL_PREFIX);
}

/** Re-encode an image as WebP no larger than `side`, or null when it would not help. */
async function shrinkImage(file: Blob, side: number, quality: number): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file); } catch { return null; }
  const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) { bitmap.close(); return null; }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob?.type === 'image/webp' ? blob : null), 'image/webp', quality));
}

async function put(documentId: string, body: Blob, name: string): Promise<{ id: string; url: string; size: number; mimeType: string }> {
  const response = await fetch(`/api/files?document=${encodeURIComponent(documentId)}&name=${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { authorization: `Bearer ${await bearer()}`, 'content-type': body.type },
    body,
  });
  if (response.ok) return response.json() as Promise<{ id: string; url: string; size: number; mimeType: string }>;
  const payload = await response.json().catch(() => null) as { error?: string; key?: string; limit?: string } | null;
  if (payload?.error === 'plan_limit') throw new UploadError({ reason: 'plan_limit', key: payload.key ?? '', limit: payload.limit ?? '' });
  if (payload?.error === 'email_unverified') throw new UploadError({ reason: 'email_unverified' });
  if (response.status === 429) throw new UploadError({ reason: 'rate_limited' });
  if (response.status === 403) throw new UploadError({ reason: 'forbidden' });
  if (response.status === 422) throw new UploadError({ reason: 'type' });
  throw new UploadError({ reason: 'failed' });
}

function webpName(name: string): string {
  return `${name.replace(/\.[^.]+$/, '') || 'image'}.webp`;
}

/**
 * Upload one file for a document and describe it as a cell attachment.
 * Photos are downscaled to WebP first, and get a small thumbnail for the grid;
 * GIFs keep their animation and PDFs go as they are.
 */
export async function uploadAttachment(documentId: string, file: File): Promise<CellAttachment> {
  if (!(ACCEPTED as readonly string[]).includes(file.type)) throw new UploadError({ reason: 'type' });

  let body: Blob = file;
  let name = file.name;
  let thumbnail: string | undefined;
  if (file.type.startsWith('image/') && file.type !== 'image/gif') {
    const shrunk = await shrinkImage(file, MAX_IMAGE_SIDE, 0.85);
    if (shrunk && shrunk.size < file.size) { body = shrunk; name = webpName(file.name); }
    const thumb = await shrinkImage(file, THUMB_SIDE, 0.7);
    if (thumb) thumbnail = (await put(documentId, thumb, `thumb-${webpName(file.name)}`)).url;
  }

  const stored = await put(documentId, body, name);
  return {
    id: stored.id,
    name,
    url: stored.url,
    mimeType: stored.mimeType,
    size: stored.size,
    ...(thumbnail ? { thumbnail } : {}),
    uploadedAt: new Date().toISOString(),
  };
}

const objectUrls = new Map<string, Promise<string | null>>();

/** An object URL for a stored file, fetched once per tab. Other URLs pass through. */
export function resolveFileUrl(url: string): Promise<string | null> {
  if (!isStoredFileUrl(url)) return Promise.resolve(url);
  let pending = objectUrls.get(url);
  if (!pending) {
    pending = (async () => {
      try {
        const response = await fetch(url, { headers: { authorization: `Bearer ${await bearer()}` } });
        if (!response.ok) return null;
        return URL.createObjectURL(await response.blob());
      } catch {
        return null;
      }
    })();
    void pending.then((value) => { if (value === null) objectUrls.delete(url); });
    objectUrls.set(url, pending);
  }
  return pending;
}

/**
 * The attachments with stored-file URLs swapped for displayable object URLs.
 *
 * `preview` resolves only what a grid cell shows — the thumbnail, or the image
 * itself when it has none — so scrolling a sheet never downloads full photos
 * or PDFs. `full` resolves everything, for the viewer.
 */
export function useResolvedAttachments(attachments: readonly CellAttachment[], mode: 'preview' | 'full'): CellAttachment[] {
  const wanted = (attachment: CellAttachment): string[] => {
    if (mode === 'full') return [attachment.url, attachment.thumbnail].filter(isStoredFileUrl);
    if (isStoredFileUrl(attachment.thumbnail)) return [attachment.thumbnail];
    return attachment.mimeType.startsWith('image/') && isStoredFileUrl(attachment.url) ? [attachment.url] : [];
  };
  const urls = [...new Set(attachments.flatMap(wanted))];
  const signature = urls.join('\n');
  const [resolved, setResolved] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    for (const url of urls) {
      void resolveFileUrl(url).then((objectUrl) => {
        if (active && objectUrl) setResolved((current) => current[url] === objectUrl ? current : { ...current, [url]: objectUrl });
      });
    }
    return () => { active = false; };
    // The signature is `urls`, joined.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return attachments.map((attachment) => {
    const url = isStoredFileUrl(attachment.url) ? resolved[attachment.url] ?? '' : attachment.url;
    const thumbnail = isStoredFileUrl(attachment.thumbnail) ? resolved[attachment.thumbnail] : attachment.thumbnail;
    return { ...attachment, url, ...(thumbnail ? { thumbnail } : {}) };
  });
}
